import { useQuery } from '@tanstack/react-query';
import { makeCurrentUserQueryKeyPrefix } from 'features/currentUser/currentUserQueries';
import { getIsAuthenticated } from 'features/customer/store/selectors';
import { httpCustomGetWorkspaceInfo } from 'services/backofficeIntegration/http/endpoints/workspace/httpCustomGetWorkspaceInfo';
import { useAppSelector } from 'store/hooks';
import { queryClient } from 'utils/ReactQueryClient';
import { assertNonNullable } from 'utils/typescript/nonNullable';

export const getQueryKey = (workspaceId?: string | null) => {
  const key = [...makeCurrentUserQueryKeyPrefix(), 'workspace-info'];

  if (workspaceId) {
    key.push(workspaceId);
  }

  return key;
};

type UseGetWorkspaceInfoOptions = {
  targetWorkspaceId?: string | null;
  enabled?: boolean;
};

export const useGetWorkspaceInfo = ({
  targetWorkspaceId,
  enabled = true,
}: UseGetWorkspaceInfoOptions = {}) => {
  const customerWorkspaceId = useAppSelector(state => state.workspace.workspaceId);
  const isAuthenticated = useAppSelector(getIsAuthenticated);

  const workspaceIdToCheck = targetWorkspaceId ?? customerWorkspaceId;

  return useQuery({
    enabled: enabled && isAuthenticated && !!workspaceIdToCheck,
    queryKey: getQueryKey(workspaceIdToCheck),
    queryFn: () => {
      assertNonNullable(workspaceIdToCheck);

      return httpCustomGetWorkspaceInfo.callEndpoint({ workspaceId: workspaceIdToCheck });
    },
    // Cache for 1 hour
    staleTime: 3_600_000,
  });
};

export const invalidateWorkspaceInfoQuery = (workspaceId?: string | null) =>
  queryClient.invalidateQueries({
    queryKey: getQueryKey(workspaceId),
    exact: true,
  });
